import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  COL_FINISHED_GOODS_LOTS,
  COL_PRODUCTS,
  COL_PRODUCTION_REQUEST_LINES,
  COL_SHIPMENT_LINES,
  COL_SHIPMENTS,
} from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { useAuth } from '../../context/AuthContext'
import { formatQty } from '../../lib/scheduleCalculations'
import { pb } from '../../pocketbase'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import { productionRequestLineUpdatePatch } from '../../lib/pbRecordDefaults'
import { Alert, FormField, PageHeader, PageShell } from '../../components/ui'
import { formInputProps } from '../../components/ui/FormField'
import {
  btn,
  cn,
  formActionsClass,
  formGridClass,
  inputClass,
  sectionTitleClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableWrapClass,
} from '../../lib/ui'
import type {
  FinishedGoodsLotRecord,
  ProductRecord,
  ProductionRequestLineRecord,
  ShipmentLineRecord,
  ShipmentRecord,
} from '../../types/records'

type LineDraft = {
  key: string
  pbId?: string
  requestLineId: string
  productId: string
  quantity: string
  lotId: string
}

function newLine(): LineDraft {
  return {
    key: crypto.randomUUID(),
    requestLineId: '',
    productId: '',
    quantity: '1',
    lotId: '',
  }
}

function dateOrToday(v: string): string {
  const t = v.trim()
  return t.length >= 10 ? t.slice(0, 10) : new Date().toISOString().slice(0, 10)
}

export function ShipmentFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.SHIPMENTS_WRITE)
  const isNew = id === 'new' || !id

  const [shippedAt, setShippedAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [clientName, setClientName] = useState('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<LineDraft[]>(() => [newLine()])

  const [requestLines, setRequestLines] = useState<ProductionRequestLineRecord[]>([])
  const [products, setProducts] = useState<ProductRecord[]>([])
  const [lots, setLots] = useState<FinishedGoodsLotRecord[]>([])

  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)

  const loadRefs = useCallback(async () => {
    const [rlList, pList, lotList] = await Promise.all([
      pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
        filter: 'status != "cancelled"',
        expand: 'product,request',
        sort: '-priority,plannedShipManager',
      }),
      pb.collection(COL_PRODUCTS).getFullList<ProductRecord>({ sort: 'name' }),
      pb.collection(COL_FINISHED_GOODS_LOTS).getFullList<FinishedGoodsLotRecord>({
        sort: '-producedAt',
        expand: 'product',
      }),
    ])
    setRequestLines(rlList)
    setProducts(pList)
    setLots(lotList)
  }, [])

  const loadShipment = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const ship = await pb.collection(COL_SHIPMENTS).getOne<ShipmentRecord>(id)
      setShippedAt(ship.shippedAt?.slice(0, 10) ?? '')
      setClientName(ship.clientName ?? '')
      setNotes(ship.notes ?? '')

      const shipLines = await pb.collection(COL_SHIPMENT_LINES).getFullList<ShipmentLineRecord>({
        filter: `shipment="${id}"`,
        expand: 'product,requestLine',
      })
      shipLines.sort((a, b) => (a.created ?? '').localeCompare(b.created ?? ''))
      setLines(
        shipLines.length > 0
          ? shipLines.map((ln) => ({
              key: ln.id,
              pbId: ln.id,
              requestLineId: typeof ln.requestLine === 'string' ? ln.requestLine : '',
              productId: typeof ln.product === 'string' ? ln.product : '',
              quantity: String(ln.quantity ?? ''),
              lotId: typeof ln.lot === 'string' ? ln.lot : '',
            }))
          : [newLine()],
      )
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки отгрузки'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void loadRefs()
  }, [loadRefs])

  useEffect(() => {
    if (!isNew) void loadShipment()
  }, [isNew, loadShipment])

  const requestLineOptions = useMemo(
    () =>
      requestLines.map((ln) => {
        const product = ln.expand?.product
        const client = ln.expand?.request?.clientName ?? ''
        const remaining = Math.max(0, Number(ln.quantity) - Number(ln.shippedQty ?? 0))
        return {
          id: ln.id,
          label: `${product?.name ?? ln.product} · ${formatQty(ln.quantity)} · ост. ${formatQty(remaining)}${client ? ` · ${client}` : ''}`,
          productId: typeof ln.product === 'string' ? ln.product : product?.id ?? '',
          clientName: client,
          remaining,
        }
      }),
    [requestLines],
  )

  function patchLine(key: string, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)))
  }

  function addLine() {
    setLines((prev) => [...prev, newLine()])
  }

  function removeLine(key: string) {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((ln) => ln.key !== key)))
  }

  function onRequestLinePick(key: string, requestLineId: string) {
    const opt = requestLineOptions.find((o) => o.id === requestLineId)
    patchLine(key, {
      requestLineId,
      productId: opt?.productId ?? '',
      quantity: opt && opt.remaining > 0 ? String(opt.remaining) : '1',
    })
    if (opt?.clientName && !clientName.trim()) {
      setClientName(opt.clientName)
    }
  }

  async function save() {
    if (!canWrite) return
    setErr('')

    if (!clientName.trim()) {
      setErr('Укажите клиента.')
      return
    }

    const validLines = lines.filter((ln) => ln.productId && Number(ln.quantity) > 0)
    if (validLines.length === 0) {
      setErr('Добавьте хотя бы одну строку с продуктом и количеством.')
      return
    }

    setSaving(true)
    try {
      let shipmentId = id
      const header = {
        shippedAt: dateOrToday(shippedAt),
        clientName: clientName.trim(),
        notes: notes.trim() || '',
      }

      if (isNew) {
        const created = await pb.collection(COL_SHIPMENTS).create(header)
        shipmentId = created.id
      } else if (shipmentId) {
        await pb.collection(COL_SHIPMENTS).update(shipmentId, header)
        const existing = await pb.collection(COL_SHIPMENT_LINES).getFullList<ShipmentLineRecord>({
          filter: `shipment="${shipmentId}"`,
        })
        for (const old of existing) {
          if (old.requestLine) {
            const rl = requestLines.find((r) => r.id === old.requestLine)
            if (rl) {
              const restored = Math.max(0, Number(rl.shippedQty ?? 0) - Number(old.quantity))
              await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(
                String(old.requestLine),
                productionRequestLineUpdatePatch(rl, {
                  shippedQty: restored,
                  status: restored >= Number(rl.quantity) ? 'shipped' : rl.status === 'shipped' ? 'completed' : rl.status,
                }),
              )
            }
          }
          await pb.collection(COL_SHIPMENT_LINES).delete(old.id)
        }
      }

      if (!shipmentId) throw new Error('Не удалось определить отгрузку')

      for (const ln of validLines) {
        const qty = Number(ln.quantity)
        await pb.collection(COL_SHIPMENT_LINES).create({
          shipment: shipmentId,
          requestLine: ln.requestLineId || undefined,
          product: ln.productId,
          quantity: qty,
          lot: ln.lotId || undefined,
        })

        if (ln.requestLineId) {
          const rl = requestLines.find((r) => r.id === ln.requestLineId)
          const prevShipped = Number(rl?.shippedQty ?? 0)
          const ordered = Number(rl?.quantity ?? 0)
          const nextShipped = prevShipped + qty
          const nextStatus =
            nextShipped >= ordered ? 'shipped' : rl?.status === 'pending' ? 'scheduled' : rl?.status ?? 'scheduled'
          await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(
            ln.requestLineId,
            productionRequestLineUpdatePatch(rl ?? {}, {
              shippedQty: nextShipped,
              status: nextStatus,
              actualShip: dateOrToday(shippedAt),
            }),
          )
        }
      }

      navigate('/shipments', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <PageHeader title={isNew ? 'Новая отгрузка' : 'Отгрузка'} />
        <p className="text-sm text-su-text">Загрузка…</p>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHeader
        title={isNew ? 'Новая отгрузка' : 'Отгрузка'}
        lead="Шапка отгрузки и строки по продукции; при привязке к строке заявки обновляется отгруженное количество."
      />
      {err && <Alert kind="err">{err}</Alert>}

      <div className={formGridClass}>
        <FormField label="Дата отгрузки" htmlFor="ship-date">
          <input
            id="ship-date"
            type="date"
            {...formInputProps()}
            value={shippedAt}
            onChange={(e) => setShippedAt(e.target.value)}
            disabled={!canWrite}
          />
        </FormField>
        <FormField label="Клиент" htmlFor="ship-client">
          <input
            id="ship-client"
            {...formInputProps()}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            disabled={!canWrite}
          />
        </FormField>
        <FormField label="Примечание" htmlFor="ship-notes">
          <textarea
            id="ship-notes"
            className={cn(inputClass, 'min-h-16 resize-y')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canWrite}
          />
        </FormField>
      </div>

      <h3 className={cn(sectionTitleClass, 'mt-6')}>Строки отгрузки</h3>
      <div className={tableWrapClass}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Строка заявки</th>
              <th className={tableHeadClass}>Продукция</th>
              <th className={tableHeadClass}>Кол-во</th>
              <th className={tableHeadClass}>Партия ГП</th>
              <th className={tableHeadClass} />
            </tr>
          </thead>
          <tbody>
            {lines.map((ln) => {
              const productLots = lots.filter(
                (lot) => (typeof lot.product === 'string' ? lot.product : lot.expand?.product?.id) === ln.productId,
              )
              return (
                <tr key={ln.key}>
                  <td className={tableCellClass}>
                    <select
                      className={inputClass}
                      value={ln.requestLineId}
                      onChange={(e) => onRequestLinePick(ln.key, e.target.value)}
                      disabled={!canWrite}
                    >
                      <option value="">— без привязки —</option>
                      {requestLineOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={tableCellClass}>
                    <select
                      className={inputClass}
                      value={ln.productId}
                      onChange={(e) => patchLine(ln.key, { productId: e.target.value, lotId: '' })}
                      disabled={!canWrite}
                    >
                      <option value="">— выберите —</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} {p.code?.trim() ? `(${p.code})` : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={tableCellClass}>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      className={cn(inputClass, 'max-w-28')}
                      value={ln.quantity}
                      onChange={(e) => patchLine(ln.key, { quantity: e.target.value })}
                      disabled={!canWrite}
                    />
                  </td>
                  <td className={tableCellClass}>
                    <select
                      className={inputClass}
                      value={ln.lotId}
                      onChange={(e) => patchLine(ln.key, { lotId: e.target.value })}
                      disabled={!canWrite || !ln.productId}
                    >
                      <option value="">— не указана —</option>
                      {productLots.map((lot) => (
                        <option key={lot.id} value={lot.id}>
                          {lot.lotCode?.trim() || lot.id.slice(0, 8)} · {formatQty(Number(lot.quantity))}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={tableCellClass}>
                    {canWrite && (
                      <button type="button" className={btn('ghost', 'sm')} onClick={() => removeLine(ln.key)}>
                        Удалить
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <div className={formActionsClass}>
          <button type="button" className={btn()} onClick={addLine}>
            Добавить строку
          </button>
        </div>
      )}

      <div className={cn(formActionsClass, 'mt-4')}>
        <Link to="/shipments" className={btn()}>
          К списку
        </Link>
        {canWrite && (
          <button type="button" className={btn('primary')} disabled={saving} onClick={() => void save()}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        )}
      </div>
    </PageShell>
  )
}
